import { useState } from 'react';
import { Button, FlexBox, Icon } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { WizardOptionList, WizardStep } from '../../WizardStep';
import { ReferenceProjectsPicker } from './LazyReferenceProjectsPicker';
import { FolderPlusIcon, FolderXIcon } from './icons';
import {
    REFERENCE_PROJECTS_DECISION_WITH,
    REFERENCE_PROJECTS_DECISION_WITHOUT,
} from './parseReply';
import type { WizardStepDefinition } from '../../WizardStep';
import type { ReferenceProjectsOptions } from './parseOptions';
import type { ReferenceProjectsReply } from './parseReply';

export type ReferenceProjectsSelectorViewProps = {
    options: ReferenceProjectsOptions;
    onSubmit: (reply: ReferenceProjectsReply) => void;
    /**
     * Asks the assistant to explain the choices. Required, not optional: step 1's footer lays the
     * help and Next actions out with `space-between`, so an absent help button would silently pull
     * Next to the left edge rather than degrade gracefully.
     */
    onRequestHelp: () => void;
};

const PICKER_STEP = 2;

/**
 * True when the customer has just come back from the sign-in redirect the picker triggers.
 *
 * Reads `window.location.search` directly, as the pilot does, rather than `useSearchParams`: this
 * library has no react-router dependency, and taking one on for a single query-string read would
 * make the selector unrenderable outside a Router (Storybook, unit tests).
 */
function returnedFromSignIn(): boolean {
    try {
        return new URLSearchParams(window.location.search).get('withReferenceProjects') === '1';
    } catch {
        return false;
    }
}

/**
 * The reference-projects composer selector: a two-step wizard over the decision and, if the
 * customer opts in, the project picker.
 *
 * Step 2 only exists once the customer has chosen to reference projects. `WizardStep` hides its
 * navigation entirely for a single-step wizard, so passing one step or two reproduces the pilot's
 * behaviour of revealing the chevrons and "n of N" counter only after "Yes" is picked.
 *
 * Renders only the card. Where that card sits — the composer's background band and centred
 * max-width column — belongs to whatever hosts the composer, exactly as it does for ChatInput.
 */
export function ReferenceProjectsSelectorView({
    options,
    onSubmit,
    onRequestHelp,
}: ReferenceProjectsSelectorViewProps) {
    const hasPreselectedWorkIds = (options.preselectedWorkIds?.length ?? 0) > 0;
    const startAtPicker =
        returnedFromSignIn() ||
        hasPreselectedWorkIds ||
        options.preselectedDecision === REFERENCE_PROJECTS_DECISION_WITH;

    const [activeStep, setActiveStep] = useState(startAtPicker ? PICKER_STEP : 1);
    const [selectedDecision, setSelectedDecision] = useState<string>(
        startAtPicker ? REFERENCE_PROJECTS_DECISION_WITH : options.preselectedDecision ?? ''
    );
    const [selectedWorkIds, setSelectedWorkIds] = useState<string[]>(
        options.preselectedWorkIds ?? []
    );
    const [helpRequested, setHelpRequested] = useState(false);

    const wantsReferenceProjects = selectedDecision === REFERENCE_PROJECTS_DECISION_WITH;

    const handleNext = () => {
        if (selectedDecision === REFERENCE_PROJECTS_DECISION_WITHOUT) {
            onSubmit({ decision: REFERENCE_PROJECTS_DECISION_WITHOUT });
            return;
        }

        if (wantsReferenceProjects) {
            setActiveStep(PICKER_STEP);
        }
    };

    // One help request per selector: the answer arrives as the next assistant turn, and a second
    // identical request before then would just duplicate the message in the thread.
    const handleRequestHelp = () => {
        setHelpRequested(true);
        onRequestHelp();
    };

    const decisionStep: WizardStepDefinition = {
        title: options.title?.trim()
            ? options.title
            : __('Would you like to reference existing projects?', {
                  _context:
                      'Heading for the chat brief step asking whether the customer wants to reference their past projects',
              }),
        content: (
            <WizardOptionList
                aria-label={__('Reference projects selection', {
                    _context:
                        'Accessible group label for the yes/no choice about referencing past projects',
                })}
                options={[
                    {
                        value: REFERENCE_PROJECTS_DECISION_WITHOUT,
                        icon: <FolderXIcon />,
                        label: __('No, continue without', {
                            _context:
                                'Option declining to reference any past projects in the chat brief',
                        }),
                    },
                    {
                        value: REFERENCE_PROJECTS_DECISION_WITH,
                        icon: <FolderPlusIcon />,
                        label: __('Yes, select projects', {
                            _context:
                                'Option choosing to reference past projects in the chat brief',
                        }),
                    },
                ]}
                selectedValue={selectedDecision}
                onSelectedValueChange={setSelectedDecision}
            />
        ),
        footer: (
            <FlexBox justifyContent="space-between" alignItems="center" gap={3}>
                <Button
                    skin="tertiary"
                    iconPosition="left"
                    onClick={handleRequestHelp}
                    disabled={helpRequested}
                >
                    <Icon iconType="aiSupport" />
                    {__('Help me answer')}
                </Button>
                <Button skin="secondary" onClick={handleNext} disabled={selectedDecision === ''}>
                    {__('Next', {
                        _context:
                            'Button advancing from the reference-projects question to the project picker',
                    })}
                </Button>
            </FlexBox>
        ),
    };

    const pickerStep: WizardStepDefinition = {
        title: __('Select from your Vistaprint projects to guide your designer’s work', {
            _context: 'Heading for the chat brief step where the customer picks past projects',
        }),
        content: (
            <ReferenceProjectsPicker
                onSelectionChange={setSelectedWorkIds}
                initialWorkIds={options.preselectedWorkIds}
            />
        ),
        footer: (
            <FlexBox justifyContent="flex-end" alignItems="center" gap={3}>
                <Button
                    skin="secondary"
                    onClick={() =>
                        onSubmit({
                            decision: REFERENCE_PROJECTS_DECISION_WITH,
                            workIds: selectedWorkIds,
                        })
                    }
                    disabled={selectedWorkIds.length === 0}
                >
                    {__('Continue')}
                </Button>
            </FlexBox>
        ),
    };

    return (
        <WizardStep
            steps={wantsReferenceProjects ? [decisionStep, pickerStep] : [decisionStep]}
            activeStep={activeStep}
            onStepChange={setActiveStep}
            previousStepLabel={__('Previous step', {
                _context: 'Accessible label for the back chevron in the chat brief wizard',
            })}
            nextStepLabel={__('Next step', {
                _context: 'Accessible label for the forward chevron in the chat brief wizard',
            })}
        />
    );
}
